import { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiList } from 'react-icons/fi';
import ProjectModal from '../../components/ProjectModal';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../services/api';
import { Link } from 'react-router-dom';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ client: '', status: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searching, setSearching] = useState(false);
  const [descPopup, setDescPopup] = useState(null);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchClients();
    fetchUsers();
    fetchProjects(1, true);
  }, []);

  const searchRef = useRef(false);
  useEffect(() => {
    if (!searchRef.current) { searchRef.current = true; return; }
    const timer = setTimeout(() => fetchProjects(1), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, filters]);

  const fetchProjects = async (page = currentPage, isInitial = false) => {
    try {
      if (isInitial) setLoading(true); else setSearching(true);
      const response = await api.post('/projects/list', {
        search: searchQuery || undefined,
        client: filters.client || undefined,
        status: filters.status || undefined,
        page,
        limit: itemsPerPage
      });
      setProjects(response.data.projects);
      setFilteredProjects(response.data.projects);
      setTotalPages(response.data.pagination.totalPages);
      setTotalCount(response.data.pagination.total);
      setCurrentPage(page);
      if (isInitial) setLoading(false); else setSearching(false);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
      if (isInitial) setLoading(false); else setSearching(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients((response.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        params: { limit: 1000 }
      });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleCreateProject = () => {
    setCurrentProject(null);
    setShowProjectModal(true);
  };

  const handleEditProject = async (project) => {
    try {
      const response = await api.get(`/projects/${project.id}`);
      setCurrentProject(response.data);
    } catch {
      setCurrentProject(project);
    }
    setShowProjectModal(true);
  };

  const handleDeleteProject = (project) => {
    setCurrentProject(project);
    setShowDeleteModal(true);
  };

  const handleSaveProject = async (projectData) => {
    try {
      if (currentProject) {
        // Update existing project
        await api.put(`/projects/${currentProject.id}`, projectData);
      } else {
        // Create new project
        await api.post('/projects', projectData);
      }
      setShowProjectModal(false);
      fetchProjects();
    } catch (err) {
      console.error('Error saving project:', err);
      alert('Failed to save project. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/projects/${currentProject.id}`);
      setShowDeleteModal(false);
      fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const clearFilters = () => {
    setFilters({ client: '', status: '' });
    setSearchQuery('');
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const goToPage = (page) => {
    setCurrentPage(page);
    fetchProjects(page);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case 'on_hold':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">On Hold</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Completed</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Projects</h1>
        <div className="text-center py-8">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Projects</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchProjects} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Projects</h1>
        <button
          onClick={handleCreateProject}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" /> Create Project
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name or description..."
                className="border border-gray-300 rounded-md px-3 py-2 text-sm w-64"
              />
              {searching && <span className="absolute right-2 top-2 text-xs text-gray-400">...</span>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
              value={filters.client}
              onChange={(e) => handleFilterChange('client', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.name}>{client.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 mb-4">No projects found</p>
          <button
            onClick={handleCreateProject}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Create Your First Project
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timeline
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      <Link to={`/admin/projects/${project.id}/tasks`} className="text-blue-600 hover:underline">
                        {project.name}
                      </Link>
                    </div>
                    {project.description && (
                      <div>
                        <div
                          className="text-sm text-gray-500 truncate max-w-xs"
                          dangerouslySetInnerHTML={{ __html: project.description.replace(/<[^>]*>/g, ' ').trim().slice(0, 60) + (project.description.replace(/<[^>]*>/g, '').length > 60 ? '...' : '') }}
                        />
                        <button
                          onClick={() => setDescPopup(project)}
                          className="text-xs text-blue-600 hover:underline mt-0.5"
                        >
                          Read more
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {project.Client ? project.Client.name : 'No Client'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(project.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(project.start_date)} - {formatDate(project.end_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <Link to={`/admin/projects/${project.id}/tasks`} className="text-indigo-600 hover:text-indigo-900">
                        <FiList size={18} title="View Tasks" />
                      </Link>
                      <button
                        onClick={() => handleEditProject(project)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FiEdit2 size={18} title="Edit Project" />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 size={18} title="Delete Project" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{startIndex + filteredProjects.length}</span> of{' '}
                <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showProjectModal && (
        <ProjectModal
          project={currentProject}
          onClose={() => setShowProjectModal(false)}
          onSave={handleSaveProject}
          clients={clients}
          users={users}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Project"
          message={`Are you sure you want to delete "${currentProject.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Description Popup */}
      {descPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDescPopup(null)}>
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div
            className="relative bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Doc-style header */}
            <div className="flex items-center justify-between px-8 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{descPopup.name}</h2>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {descPopup.Client && <span>Client: <span className="font-medium text-gray-700">{descPopup.Client.name}</span></span>}
                  <span>Status: <span className="font-medium text-gray-700 capitalize">{descPopup.status?.replace('_', ' ')}</span></span>
                  {descPopup.start_date && <span>Start: <span className="font-medium text-gray-700">{formatDate(descPopup.start_date)}</span></span>}
                  {descPopup.end_date && <span>End: <span className="font-medium text-gray-700">{formatDate(descPopup.end_date)}</span></span>}
                </div>
              </div>
              <button onClick={() => setDescPopup(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            {/* Doc-style body */}
            <div className="overflow-y-auto px-8 py-6">
              <div
                className="prose prose-sm max-w-none text-gray-800"
                dangerouslySetInnerHTML={{ __html: descPopup.description }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;